import { CONSTANTS } from '../game.js';
import { AbstractTarget } from './AbstractTarget.js';
import { SoundManager } from '../managers/SoundManager.js';

/**
 * アクティブターゲットクラス
 * 現在プレイヤーが狙うべきアクティブな的を表現
 */
export class ActiveTarget extends AbstractTarget {
    /**
     * アクティブターゲットのコンストラクタ
     * @param {Phaser.Scene} scene - シーンオブジェクト
     * @param {number} x - X座標
     * @param {number} y - Y座標
     */
    constructor(scene, x, y, targetType = null, order = 0, isMoving = false, initialVelocity = null) {
        super(scene, x, y, targetType, order);
        
        // 移動状態を引き継ぐ
        if (isMoving) {
            this.isMoving = isMoving;
            if (initialVelocity) {
                this.velocity = { ...initialVelocity };
            } else {
                this.initializeMovement();
            }
        }
        
        // テクスチャを種類に応じたものに設定
        const textureName = this.getTextureNameByType();
        this.sprite.setTexture(textureName);
        this.container.setAlpha(1.0); // 完全に不透明
        
        // 物理ボディをセンサーとして設定（衝突検出可能）
        this.scene.matter.body.set(this.body, {
            isSensor: true,
            collisionFilter: { 
                group: 0,
                category: 0x0002,
                mask: 0xFFFFFFFF  // すべてのカテゴリと衝突する
            }
        });
    }

    /**
     * ターゲットの色を取得
     * @returns {number} ターゲットの色
     */
    getTargetColor() {
        // 常に黄色を返す
        return CONSTANTS.EFFECTS.SPLASH.COLORS.YELLOW;
    }
    
    /**
     * ヒットエフェクトの作成
     * @param {string} evaluation - 評価（'FAIL', 'PASS', 'GOOD', 'GREAT', 'PERFECT'）
     */
    createHitEffect(evaluation) {
        // body が存在するか確認
        if (!this.body || !this.body.position) return;
        
        const x = this.body.position.x;
        const y = this.body.position.y;
        
        // 飛沫エフェクトを作成
        this.createSplashParticles(x, y, evaluation);
        
        // 評価に応じた効果音を再生
        this.playHitSound(evaluation);
    }
    
    /**
     * 評価に応じた効果音を再生
     * @param {string} evaluation - 評価（'FAIL', 'PASS', 'GOOD', 'GREAT', 'PERFECT'）
     */
    playHitSound(evaluation) {
        // SoundManagerを使用
        const soundManager = new SoundManager(this.scene);
        soundManager.playTargetHitSound(evaluation);
    }

    /**
     * 飛沫エフェクトの作成
     */
    createSplashParticles(x, y, evaluation) {
        // ポイント設定を取得
        let points = CONSTANTS.EFFECTS.SPLASH.POINTS[evaluation] || CONSTANTS.EFFECTS.SPLASH.POINTS.FAIL;
        
        // ターゲットの色を取得
        const color = this.getTargetColor();
        
        const config = {
            speed: CONSTANTS.EFFECTS.SPLASH.SPEED,
            scale: { 
                start: CONSTANTS.EFFECTS.SPLASH.SCALE.start * points.scale, 
                end: CONSTANTS.EFFECTS.SPLASH.SCALE.end 
            },
            lifespan: Math.floor(CONSTANTS.EFFECTS.SPLASH.BASE_LIFESPAN * points.lifespan),
            quantity: Math.floor(CONSTANTS.EFFECTS.SPLASH.BASE_QUANTITY * points.quantity),
            gravityY: CONSTANTS.EFFECTS.SPLASH.GRAVITY,
            alpha: { start: 1, end: 0 },
            blendMode: 'ADD',
            emitting: false,
            emitZone: {
                type: 'random',
                source: new Phaser.Geom.Circle(0, 0, CONSTANTS.EFFECTS.SPLASH.CIRCLE_RADIUS),
                quantity: Math.floor(CONSTANTS.EFFECTS.SPLASH.BASE_QUANTITY * points.quantity)
            },
            tint: color
        };

        const particles = this.scene.add.particles(x, y, 'target', config);
        particles.setDepth(16);
        particles.explode(config.quantity, 0, 0);
        
        this.scene.time.delayedCall(config.lifespan + 100, () => {
            particles.destroy();
        });
    }
}
